import { useInfiniteQuery } from '@tanstack/react-query';
import { api, type ChatMessageResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Server-side cap; the route rejects anything longer. */
export const CHAT_SEARCH_MAX_QUERY_LENGTH = 500;

/**
 * Postgres `plainto_tsquery` needs a whole word to match, so a one-letter prefix
 * returns nothing useful and just burns a request per keystroke. Only applies to
 * the text terms — a sender filter alone is a valid search.
 */
export const CHAT_SEARCH_MIN_QUERY_LENGTH = 2;

export interface UseChatMessageSearchResult {
  results: ChatMessageResponse[];
  /** Total matches in the channel, from the first page. */
  total: number;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * Search over one channel's messages, cursor-paginated. Backed by
 * `GET /arches/chat/channels/:channelId/messages/search`, which accepts full-text
 * terms, a sender username, or both — so "everything Bob wrote here" is a search
 * with no text at all.
 *
 * Only runs while `enabled` (the panel is open) and at least one filter is
 * usable, so a closed panel or a half-typed word costs nothing. The caller is
 * responsible for debouncing `query`.
 */
export function useChatMessageSearch(
  channelId: string | null,
  query: string,
  senderUsername: string | null,
  enabled: boolean,
  pageSize = 25,
): UseChatMessageSearchResult {
  const trimmed = query.trim().slice(0, CHAT_SEARCH_MAX_QUERY_LENGTH);
  const from = senderUsername?.trim() || undefined;
  // Text below the minimum is treated as absent, so "de" + a sender filter still
  // searches — by sender — rather than sitting idle waiting for a longer word.
  const terms = trimmed.length >= CHAT_SEARCH_MIN_QUERY_LENGTH ? trimmed : undefined;
  const isQueryable = !!terms || !!from;

  const result = useInfiniteQuery({
    queryKey: queryKeys.chat.messageSearch(channelId ?? '', terms ?? '', from ?? ''),
    queryFn: ({ pageParam }) =>
      api.searchChatMessages(channelId!, terms ?? '', {
        from,
        limit: pageSize,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    // The server decides whether more exists (it over-fetches by one) rather
    // than the client inferring it from a count — a count-vs-page mismatch
    // caused by a concurrent delete would otherwise strand "Load more" forever.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!channelId && isQueryable,
    staleTime: 30_000,
  });

  return {
    results: result.data?.pages.flatMap((p) => p.messages) ?? [],
    // Only the first page carries a total; later pages report null.
    total: result.data?.pages[0]?.total ?? 0,
    // Nothing searchable isn't "loading" — it's idle, and the panel shows its
    // prompt rather than a spinner that would never resolve.
    isLoading: isQueryable && result.isLoading,
    isError: result.isError,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
  };
}
